/**
 * ChapterCard — one of the four chapter cards on the CMF Guide landing page.
 *
 * Full-bleed image card with a dark overlay; the title is always visible and
 * the subtitle + tag pills reveal on hover/focus (mirrors demo-v784
 * PortfolioCard). Clicking opens the chapter sub-page (CX-2726..2729):
 *   /circular-cmf-guide/:chapterSlug
 *
 * Image failures fall back to the guide default image (CX-2725 AC).
 */

import { useNavigate } from "react-router-dom";
import type { CmfChapterCard } from "../cmfGuideData";
import { CMF_GUIDE_DEFAULT_IMAGE } from "../cmfGuideData";
import "./ChapterCard.css";

interface ChapterCardProps {
  chapter: CmfChapterCard;
}

export const ChapterCard = ({ chapter }: ChapterCardProps) => {
  const navigate = useNavigate();
  const to = `/circular-cmf-guide/${chapter.slug}`;

  return (
    <button type="button" className="chapter-card" onClick={() => navigate(to)}>
      <img
        src={chapter.image}
        alt={chapter.title}
        className="chapter-card__image"
        loading="lazy"
        draggable={false}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          if (img.src.endsWith(CMF_GUIDE_DEFAULT_IMAGE)) return;
          img.src = CMF_GUIDE_DEFAULT_IMAGE;
        }}
      />
      <span className="chapter-card__darken" aria-hidden="true" />
      <span className="chapter-card__overlay" aria-hidden="true" />

      <div className="chapter-card__content">
        <h3 className="chapter-card__title">{chapter.title}</h3>
        <div className="chapter-card__reveal">
          <span className="chapter-card__divider" aria-hidden="true" />
          <p className="chapter-card__subtitle">{chapter.subtitle}</p>
          {chapter.tags.length > 0 && (
            <div className="chapter-card__tags">
              {chapter.tags.map((tag) => (
                <span key={tag} className="chapter-card__tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
